import React, { useMemo } from 'react'
import { orderBy, head } from 'lodash'
import moment from 'moment'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { Box, Typography } from 'transfix-ui/core'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { IBaseFileType, IDocumentListWrapperProps, IFile } from 'apps/fleet-planner/types'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import { UploadInputButton } from '../atoms/UploadInputButton'
import { DocumentList } from './DocumentList'

const getDocumentTitle = (doc: IBaseFileType) => {
  if (!doc.fileName || doc.fileName === 'data.pdf') {
    // use the fallback title if the filename is missing, empty, or generated by the backend
    return 'POD'
  }
  return doc.fileName
}

const getDocumentSubtitle = (document: IBaseFileType) => {
  const { createdAt = moment().format() } = document || {}
  return (
    <>
      Uploaded{' '}
      <FormattedDateTime
        inSentence
        showTimezone
        datetime={createdAt}
        format='timeDateAndYear'
        displayTimezone={moment.tz.guess() as AllowedTimezones}
      />
    </>
  )
}

/**
 * Displays POD files from Fleet Planner's ILoad['pods']
 */
export function DocumentPodList<T extends IBaseFileType>({
  documents: documentsProp = [],
  rejectedDocuments,
  onChange,
  onReplace,
  isLoading = false,
  status,
  podState,
}: IDocumentListWrapperProps<T>) {
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isFmsRejectionReasonPOD } = getFlag('fmsRejectionReasonPOD')

  // Do not display current POD files if the status is POD Rejected.
  // Uploading a POD when the POD is in POD Rejected transitions the shipment status.
  const [recentRejectedDocuments] = rejectedDocuments || []
  const { podRemovalReason } = recentRejectedDocuments || {}

  const showPodReason = podRemovalReason && isFmsRejectionReasonPOD
  const podRejectedMessage =
    status === LOAD_STATE['POD Rejected'] ? (
      <div>
        <p>
          <strong>Your POD was rejected for the following reason(s):</strong>
        </p>
        <ul>
          <li>{showPodReason ? podRemovalReason : 'Please make sure your POD is easy to read.'}</li>
        </ul>
      </div>
    ) : undefined

  const pods: IBaseFileType[] = useMemo(() => {
    if (documentsProp.length === 0 || status === LOAD_STATE['POD Rejected']) {
      // exit early if there are no documents or the status is POD Rejected
      return []
    }
    // return the most recent batch of pods uploaded
    const sortedPods = orderBy(documentsProp, 'createdAt', 'desc')
    return sortedPods.filter(
      pod => moment(sortedPods[0].createdAt).diff(moment(pod.createdAt), 'seconds') <= 1
    )
  }, [documentsProp, status])

  const latestPod: IBaseFileType | undefined = useMemo(() => head(pods), [pods])

  const uploadHandler = (files: IFile[]) => {
    if (onReplace && latestPod?.url) {
      // either we are replace an existing pod
      onReplace(latestPod.url, files, 'pod')
    } else {
      // ... or we are uploading a new one
      onChange(files, 'pod')
    }
  }

  return (
    <Box>
      <Box mb={1}>
        <Typography variant='subtitle2'>Proof of Delivery</Typography>
      </Box>
      <DocumentList
        isLoading={isLoading}
        fileType='pod'
        documentName='POD'
        documents={pods}
        getDocumentTitle={getDocumentTitle}
        getDocumentSubtitle={getDocumentSubtitle}
        rejectedDocuments={rejectedDocuments}
        limit={isLoading ? 1 : -1}
        alertMessage={podRejectedMessage}
        podState={podState}
        /**
         * FP Attachments/PODs have a `createdAt` value which
         * can be used to show times here
         */
        FormattedDateTimeProps={{
          format: 'timeDateAndYear',
        }}
        UploadComponent={
          <Box pt={2} pb={2} display='flex' alignItems='center'>
            <Box flex='0 0 auto'>
              <UploadInputButton
                onUpload={uploadHandler}
                label={latestPod ? 'Replace POD' : 'Upload POD'}
                disabled={isLoading}
              />
            </Box>
            <Box flex='1 1 auto' ml={2}>
              {isLoading ? (
                <Typography variant='subtitle2' color='textSecondary' component='span'>
                  Uploading file(s)...
                </Typography>
              ) : (
                ''
              )}
            </Box>
          </Box>
        }
      />
    </Box>
  )
}
